import { PUBLIC_API_PATH } from 'config';
import { request } from './client';

/*
 * Catalog access for every page. Same envelope as the server: `{ value, count }`.
 *
 * REACT_APP_DEMO_CATALOG=true serves the bundled demo shop (src/demo),
 * loaded as a separate chunk only when the flag is on. Otherwise these call
 * the public catalog endpoints the Phase 1 server implements; the request and
 * response shapes are in docs/CATALOG_API_DRAFT.md.
 */
export const DEMO_CATALOG = process.env.REACT_APP_DEMO_CATALOG === 'true';

const demo = () => import('demo/adapter');

// UI sort ids -> server `sort` parameter (docs/CONVENTIONS.md: field:asc|desc).
const SORT_PARAM = {
  featured: 'position:asc',
  newest: 'created_at:desc',
  'price-asc': 'price:asc',
  'price-desc': 'price:desc',
};

async function viaDemo(fn, ...args) {
  const adapter = await demo();
  return adapter[fn](...args);
}

export function getHome(options) {
  if (DEMO_CATALOG) return viaDemo('getHome');
  return request({ method: 'get', url: `${PUBLIC_API_PATH}/home`, ...options });
}

let categoriesPromise = null;

/** Cached for the session: the header, footer and listing pages all need it. */
export function listCategories() {
  if (!categoriesPromise) {
    categoriesPromise = (
      DEMO_CATALOG
        ? viaDemo('listCategories')
        : request({ method: 'get', url: `${PUBLIC_API_PATH}/categories` })
    ).catch((error) => {
      categoriesPromise = null;
      throw error;
    });
  }
  return categoriesPromise;
}

// The listing query as server parameters, shared by products and filters.
function listingParams({ category, q, attributes = {}, minPrice, maxPrice }) {
  // Attribute filters travel as one parameter per key: ?skin_type=kering,normal
  const attributeParams = {};
  Object.entries(attributes).forEach(([key, values]) => {
    if (values && values.length) attributeParams[key] = values.join(',');
  });
  return {
    category: category && category !== 'semua' ? category : undefined,
    search: q || undefined,
    ...attributeParams,
    min_price: Number.isFinite(minPrice) ? minPrice : undefined,
    max_price: Number.isFinite(maxPrice) ? maxPrice : undefined,
  };
}

/**
 * @param {{ category?: string, q?: string, attributes?: Record<string, string[]>, minPrice?: number,
 *           maxPrice?: number, sort?: string, page?: number, limit?: number }} params
 */
export function listProducts(params = {}, options) {
  if (DEMO_CATALOG) return viaDemo('listProducts', params);
  const { sort, page = 1, limit = 12 } = params;
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/products`,
    params: {
      ...listingParams(params),
      sort: SORT_PARAM[sort] || undefined,
      limit,
      offset: (Math.max(1, page) - 1) * limit,
    },
    ...options,
  });
}

/**
 * Filters for a listing, counted for the same query as listProducts (search,
 * the other selected filters, price), so every count matches what ticking
 * that option shows.
 * @param {{ category?: string, q?: string, attributes?: Record<string, string[]>, minPrice?: number,
 *           maxPrice?: number }} params
 */
export function getFilters(params = {}, options) {
  if (DEMO_CATALOG) return viaDemo('getFilters', params);
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/catalog/filters`,
    params: listingParams(params),
    ...options,
  });
}

export function getProduct(slug, options) {
  if (DEMO_CATALOG) return viaDemo('getProduct', slug);
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/products/${encodeURIComponent(slug)}`,
    ...options,
  });
}

export function getRelated(slug, options) {
  if (DEMO_CATALOG) return viaDemo('getRelated', slug);
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/products/${encodeURIComponent(slug)}/related`,
    ...options,
  });
}

export function getPage(slug, options) {
  if (DEMO_CATALOG) return viaDemo('getPage', slug);
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/pages/${encodeURIComponent(slug)}`,
    ...options,
  });
}

/**
 * Articles (CMS pages of kind "article"), newest first, `{ value, count }`.
 *   GET /public/articles?limit=&offset=  ->  [{ slug, title, excerpt, publishedAt, updatedAt, image }]
 * docs/PHASE4.md decisions P1/P2: listed at /artikel, 12 per ?halaman=N.
 */
export function listArticles({ page = 1, limit = 12 } = {}, options) {
  if (DEMO_CATALOG) return viaDemo('listArticles', { page, limit });
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/articles`,
    params: { limit, offset: (Math.max(1, page) - 1) * limit },
    ...options,
  });
}

/** One article: the summary fields plus `intro`, `sections` (like a page); 404 ARTICLE_NOT_FOUND. */
export function getArticle(slug, options) {
  if (DEMO_CATALOG) return viaDemo('getArticle', slug);
  return request({
    method: 'get',
    url: `${PUBLIC_API_PATH}/articles/${encodeURIComponent(slug)}`,
    ...options,
  });
}
